import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { Icon } from '@styles/Icon';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled } from "styled-components";
import { SignInSignUpModal } from '../auth/SignInSignUpModal';
import { StyledLogoButton } from "../button/Button";
import { CartBadge } from '../cart/CartBadge';
import { GradientButton } from '../button/Button';
import { useCart } from '@/queries/useCartQueries';
import { FlexSBCBox } from '@/styles/Boxes';
import { IconButton } from '@/styles/IconButton';

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoggedIn } = useAuthStore();
  const { logout } = useAuth();

  // Fetch cart using TanStack Query
  const { data: cartData } = useCart();

  // Calculate cart item count
  const cartItemCount = useMemo(() => {
    if (cartData?.success && cartData.data?.results) {
      return cartData.data.results.length;
    }
    return 0;
  }, [cartData]);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };
  
  return (
    <StyledHeader>
      <HeaderContent>
        <Link to="/" aria-label="홈으로 이동">
          <StyledLogoButton>GRAB MUSIC</StyledLogoButton>
        </Link>

        {/* Desktop Navigation */}
        <DesktopNav aria-label="주요 메뉴">
          <ul>
            <li>
              <StyledNavLink to="/" $active={location.pathname === '/'}>
                Home
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/explore" $active={location.pathname === '/explore'}>
                Explore
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/artist" $active={location.pathname === '/artist'}>
                Artist
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/verify" $active={location.pathname === '/verify'}>
                Verify
              </StyledNavLink>
            </li>
          </ul>
        </DesktopNav>
        
        <HeaderActions>
          {isLoggedIn ? (
            <>
              <Link to="/mypage">
                <IconButton $variant="default" $size="md" aria-label="마이페이지">
                  <Icon name="user" size={24} />
                </IconButton>
              </Link>
              <Link to="/cart">
                <CartBadge count={cartItemCount}>
                  <IconButton $variant="default" $size="md" aria-label={`장바구니 (${cartItemCount}개 항목)`}>
                    <Icon name="cart" size={32} />
                  </IconButton>
                </CartBadge>
              </Link>
              <GradientButton
                onClick={handleLogout}
                variant="signout"
                size="small"
                fontSize="18px"
                aria-label="로그아웃"
              >
                Sign Out
              </GradientButton>
            </>
          ) : (
            <GradientButton
              onClick={() => setIsModalOpen(true)}
              variant="primary"
              size="small"
              fontSize="18px"
              aria-label="로그인"
            >
              Sign In
            </GradientButton>
          )}
        </HeaderActions>
        {/* Sign In/Sign Up Modal */}
        <SignInSignUpModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
        />
        
        {/* Mobile Menu Button */}
        <MobileMenuButton
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
          aria-expanded={isMobileMenuOpen}
        >
          <span />
          <span />
          <span />
        </MobileMenuButton>
      </HeaderContent>
      
      {/* Mobile Menu */}
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <MobileNavList>
          <li>
            <StyledNavLink 
              to="/" 
              $active={location.pathname === '/'}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink 
              to="/explore" 
              $active={location.pathname === '/explore'}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Explore
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink 
              to="/artist" 
              $active={location.pathname === '/artist'}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Artist
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink 
              to="/verify" 
              $active={location.pathname === '/verify'}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Verify
            </StyledNavLink>
          </li>

          {isLoggedIn && (
            <>
            <li>
              <StyledNavLink 
                to="/mypage" 
                $active={location.pathname === '/mypage'}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                My Page
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink 
                to="/cart" 
                $active={location.pathname === '/cart'}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Cart
              </StyledNavLink>
            </li>
            </>
          )}
          <li>
            {!isLoggedIn ? (
              <GradientButton 
                onClick={() => setIsModalOpen(true)}
                variant="gradient"
                size="small"
                fontSize="18px"
                width="100%"
              >
                Sign In
              </GradientButton>
            ) : (
              <GradientButton 
                onClick={handleLogout}
                variant="secondary"
                size="small"
                fontSize="18px"
                width="100%"
              >
                Sign Out
              </GradientButton>
            )}
          </li>
        </MobileNavList>
      </MobileMenu>
    </StyledHeader>
  );
}

export default Header;

const StyledHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 92px;
  background: ${({ theme }) => theme.colors.bgWhite};
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  z-index: ${({ theme }) => theme.zIndex.header};
`;

const HeaderContent = styled(FlexSBCBox)`
  height: 100%;
  padding: 0 240px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    padding: 0 120px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 0 60px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 20px;
  }
`;

const DesktopNav = styled.nav`
  flex: 1;
  display: flex;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
  
  ul {
    list-style: none;
    display: flex;
    gap: 2rem;
    margin: 0;
    padding: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
  }

  span {
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.fontBlackTab};
    transition: all 0.3s ease;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 4px;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 92px;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(${({ $isOpen }) => $isOpen ? '0' : '-100%'});
  opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 20px;
  margin: 0;
  
  li {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StyledNavLink = styled(Link)<{ $active: boolean }>`
  text-decoration: none;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme, $active }) => 
    $active ? theme.colors.primary : theme.colors.fontBlackTab};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;